import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { appealService } from '../../services/appealService';

const APPEAL_STATUSES_STORAGE_KEY = 'appealStatuses';

const loadStoredStatuses = () => {
  try {
    const stored = localStorage.getItem(APPEAL_STATUSES_STORAGE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveStoredStatuses = (statuses) => {
  try {
    localStorage.setItem(APPEAL_STATUSES_STORAGE_KEY, JSON.stringify(statuses));
  } catch {
    // Ignore storage quota and private mode errors.
  }
};

const isSameAppealRow = (prev, next) => (
  prev.Id === next.Id
  && prev.Number === next.Number
  && prev.StatusId === next.StatusId
  && prev.HasUnreadMessages === next.HasUnreadMessages
  && prev.HasAttachments === next.HasAttachments
  && prev.FirstMessage === next.FirstMessage
  && prev.OwnerName === next.OwnerName
  && prev.CreatedAt === next.CreatedAt
  && prev.Category?.Id === next.Category?.Id
  && prev.Category?.Name === next.Category?.Name
);

export const mergeAppealsList = (current = [], incoming) => {
  if (!Array.isArray(incoming)) return current;

  const prevById = new Map(current.map((item) => [item.Id, item]));
  const next = incoming.map((item) => {
    const prev = prevById.get(item.Id);
    return prev && isSameAppealRow(prev, item) ? prev : item;
  });

  const unchanged = next.length === current.length
    && next.every((item) => prevById.get(item.Id) === item);

  return unchanged ? current : next;
};

export const fetchAppeals = createAsyncThunk(
  'appeals/fetchAppeals',
  async (_, { rejectWithValue }) => {
    try {
      return await appealService.getAppeals();
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка загрузки обращений';
      return rejectWithValue(message);
    }
  }
);

export const fetchAppealsSilent = createAsyncThunk(
  'appeals/fetchAppealsSilent',
  async (_, { rejectWithValue }) => {
    try {
      return await appealService.getAppeals();
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка загрузки обращений';
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => !getState().appeals.loading,
  }
);

export const fetchAppealDetail = createAsyncThunk(
  'appeals/fetchAppealDetail',
  async ({ ticketId }, { rejectWithValue }) => {
    try {
      return await appealService.getAppeal(ticketId);
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка загрузки обращения';
      return rejectWithValue(message);
    }
  }
);

export const fetchAccountBalance = createAsyncThunk(
  'appeals/fetchAccountBalance',
  async (accountId, { rejectWithValue }) => {
    try {
      return await appealService.getAccountBalance(accountId);
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка загрузки задолженности';
      return rejectWithValue(message);
    }
  }
);

export const fetchStatuses = createAsyncThunk(
  'appeals/fetchStatuses',
  async (_, { rejectWithValue }) => {
    try {
      return await appealService.getStatuses();
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка загрузки статусов';
      return rejectWithValue(message);
    }
  }
);

export const sendMessage = createAsyncThunk(
  'appeals/sendMessage',
  async ({ ticketId, messageData }, { rejectWithValue }) => {
    try {
      return await appealService.sendMessage(ticketId, messageData);
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка отправки сообщения';
      return rejectWithValue(message);
    }
  }
);

export const uploadAttachment = createAsyncThunk(
  'appeals/uploadAttachment',
  async ({ ticketId, file, authorName, authorId }, { rejectWithValue }) => {
    try {
      return await appealService.uploadAttachment(ticketId, file, { authorName, authorId });
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка загрузки вложения';
      return rejectWithValue(message);
    }
  }
);

export const updateAppealStatus = createAsyncThunk(
  'appeals/updateAppealStatus',
  async ({ ticketId, data }, { rejectWithValue }) => {
    try {
      return await appealService.updateAppeal(ticketId, data);
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка обновления статуса';
      return rejectWithValue(message);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'appeals/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      return await appealService.getCategories();
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка загрузки категорий';
      return rejectWithValue(message);
    }
  }
);

export const markMessagesViewed = createAsyncThunk(
  'appeals/markMessagesViewed',
  async ({ ticketId, messageIds, employee }, { rejectWithValue }) => {
    try {
      return await appealService.markMessagesViewed(ticketId, messageIds, employee);
    } catch (error) {
      const message = error.response?.data?.errors?.[0] || error.message || 'Ошибка отметки прочитанных';
      return rejectWithValue(message);
    }
  }
);

const appealsSlice = createSlice({
  name: 'appeals',
  initialState: {
    appeals: [],
    currentAppeal: null,
    accountBalance: null,
    accountBalanceLoading: false,
    accountBalanceError: null,
    loading: false,
    detailLoading: false,
    error: null,
    statuses: loadStoredStatuses(),
    statusesLoading: false,
    categories: [],
    categoriesLoading: false,
    activeStatusTab: 'all',
    sendingMessage: false,
    uploadingAttachment: false,
  },
  reducers: {
    clearCurrentAppeal: (state) => {
      state.currentAppeal = null;
    },
    clearAccountBalance: (state) => {
      state.accountBalance = null;
      state.accountBalanceLoading = false;
      state.accountBalanceError = null;
    },
    setActiveStatusTab: (state, action) => {
      state.activeStatusTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppeals.fulfilled, (state, action) => {
        state.loading = false;
        state.appeals = action.payload || [];
      })
      .addCase(fetchAppeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAppealsSilent.fulfilled, (state, action) => {
        state.appeals = mergeAppealsList(state.appeals, action.payload);
      })
      .addCase(fetchAppealDetail.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchAppealDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentAppeal = action.payload;
      })
      .addCase(fetchAppealDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAccountBalance.pending, (state) => {
        state.accountBalanceLoading = true;
        state.accountBalanceError = null;
      })
      .addCase(fetchAccountBalance.fulfilled, (state, action) => {
        state.accountBalanceLoading = false;
        state.accountBalance = action.payload;
      })
      .addCase(fetchAccountBalance.rejected, (state, action) => {
        state.accountBalanceLoading = false;
        state.accountBalance = null;
        state.accountBalanceError = action.payload;
      })
      .addCase(fetchStatuses.pending, (state) => {
        state.statusesLoading = true;
      })
      .addCase(fetchStatuses.fulfilled, (state, action) => {
        state.statusesLoading = false;
        const statuses = Array.isArray(action.payload) ? action.payload : [];
        state.statuses = statuses;
        saveStoredStatuses(statuses);
      })
      .addCase(fetchStatuses.rejected, (state, action) => {
        state.statusesLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesLoading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload || [];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.error = action.payload;
      })
      .addCase(sendMessage.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        state.currentAppeal = action.payload;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload;
      })
      .addCase(uploadAttachment.pending, (state) => {
        state.uploadingAttachment = true;
      })
      .addCase(uploadAttachment.fulfilled, (state, action) => {
        state.uploadingAttachment = false;
        state.currentAppeal = action.payload;
      })
      .addCase(uploadAttachment.rejected, (state, action) => {
        state.uploadingAttachment = false;
        state.error = action.payload;
      })
      .addCase(markMessagesViewed.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentAppeal = action.payload;
        }
      })
      .addCase(updateAppealStatus.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentAppeal = action.payload;
        }
      });
  },
});

export const { clearCurrentAppeal, clearAccountBalance, setActiveStatusTab } = appealsSlice.actions;
export default appealsSlice.reducer;
